import React, { useState, useRef } from 'react';
import { downloadTemplate as downloadTemplateAPI } from '../../../models/budgetsModel';

function BudgetImport({ isOpen, onClose, onImport }) {
  const [file, setFile] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewData, setPreviewData] = useState(null);
  const [errors, setErrors] = useState({});
  const [importSettings, setImportSettings] = useState({
    fromYear: new Date().getFullYear().toString(),
    toYear: (new Date().getFullYear() + 1).toString(),
    replaceExisting: false
  });
  const fileInputRef = useRef(null);

  const acceptedFormats = ['.csv', '.xlsx'];
  const maxFileSize = 10 * 1024 * 1024; // 10MB

  const resetState = () => {
    setFile(null);
    setPreviewData(null);
    setErrors({});
    setUploadProgress(0);
    setIsUploading(false);
  };

  const validateFile = (selectedFile) => {
    const newErrors = {};

    if (!selectedFile) {
      newErrors.file = 'Please select a file';
      setErrors(newErrors);
      return false;
    }

    // Check file size
    if (selectedFile.size > maxFileSize) {
      newErrors.file = 'File size must be less than 10MB';
      setErrors(newErrors);
      return false;
    }

    // Check file format
    const fileExtension = selectedFile.name.toLowerCase().substring(selectedFile.name.lastIndexOf('.'));
    if (!acceptedFormats.includes(fileExtension)) {
      newErrors.file = `File format not supported. Please use: ${acceptedFormats.join(', ')}`;
      setErrors(newErrors);
      return false;
    }

    setErrors({});
    return true;
  };

  const handleFileSelect = (selectedFile) => {
    if (validateFile(selectedFile)) {
      setFile(selectedFile);
      generatePreview(selectedFile);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    handleFileSelect(selectedFile);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const droppedFile = e.dataTransfer.files[0];
    handleFileSelect(droppedFile);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const generatePreview = (file) => {
    // For demo purposes, generate mock preview data
    // In a real implementation, you would parse the actual file
    const mockData = {
      totalRows: 25,
      columns: ['GL Code', 'Description', 'Year', 'Month', 'Total Amount', 'Item Name', 'Item Amount'],
      sampleRows: [
        {
          'GL Code': '4001',
          'Description': 'Office Supplies',
          'Year': '2024',
          'Month': '1',
          'Total Amount': '5000.00',
          'Item Name': 'Stationery',
          'Item Amount': '1500.00'
        },
        {
          'GL Code': '4001',
          'Description': 'Office Supplies',
          'Year': '2024',
          'Month': '1',
          'Total Amount': '5000.00',
          'Item Name': 'Computer Equipment',
          'Item Amount': '2000.00'
        },
        {
          'GL Code': '4002',
          'Description': 'Marketing Expenses',
          'Year': '2024',
          'Month': '2',
          'Total Amount': '10000.00',
          'Item Name': 'Online Ads',
          'Item Amount': '7500.00'
        }
      ]
    };

    setPreviewData(mockData);
  };

  const handleImport = async () => {
    if (!file) {
      setErrors({ file: 'Please select a file' });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('year', new Date().getFullYear());

      // Call the import function
      await onImport(formData);

      setUploadProgress(100);
      
      // Close modal after successful import
      setTimeout(() => {
        onClose();
        resetState();
      }, 1000);

    } catch (error) {
      console.error('Import error:', error);
      setErrors({ import: 'Failed to import budget data. Please check the file format and try again.' });
    } finally {
      setIsUploading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      await downloadTemplateAPI();
    } catch (error) {
      console.error('Error downloading template:', error);
      setErrors({ template: 'Failed to download template. Please try again.' });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-medium text-gray-900">Import Budget Data</h3>
          <button
            onClick={() => {
              onClose();
              resetState();
            }}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-6">
          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <h4 className="text-sm font-medium text-blue-800 mb-2">Import Instructions</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Upload a CSV or Excel (.xlsx) file</li>
              <li>• Maximum file size: 10MB</li>
              <li>• Required columns: GL Code, Description, Year, Month, Total Amount</li>
              <li>• Optional columns: Item Name, Item Amount</li>
              <li>• Items with the same GL Code, Year, and Month will be grouped under the same budget</li>
              <li>• Download the template below for the correct format</li>
            </ul>
          </div>

          {/* Download Template */}
          <div className="flex justify-between items-center">
            <h4 className="text-md font-medium text-gray-900">Step 1: Download Template</h4>
            <button
              onClick={downloadTemplate}
              className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Download Template
            </button>
          </div>

          {/* File Upload */}
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-4">Step 2: Upload Your File</h4>
            
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center ${
                isDragOver
                  ? 'border-blue-400 bg-blue-50'
                  : errors.file
                  ? 'border-red-400 bg-red-50'
                  : 'border-gray-300 bg-gray-50'
              }`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              {file ? (
                <div className="space-y-2">
                  <svg className="mx-auto h-12 w-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{file.name}</p>
                    <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                  </div>
                  <button
                    onClick={() => {
                      setFile(null);
                      setPreviewData(null);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                    className="text-red-600 hover:text-red-800 text-sm font-medium"
                  >
                    Remove File
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <div>
                    <p className="text-sm text-gray-600">
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="font-medium text-blue-600 hover:text-blue-500"
                      >
                        Click to upload
                      </button>
                      {' '}or drag and drop
                    </p>
                    <p className="text-xs text-gray-500">
                      CSV, XLSX up to 10MB
                    </p>
                  </div>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileChange}
                accept={acceptedFormats.join(',')}
                className="hidden"
              />
            </div>

            {errors.file && (
              <p className="mt-2 text-sm text-red-600">{errors.file}</p>
            )}
          </div>

          {/* File Preview */}
          {previewData && (
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-4">Step 3: Preview Data</h4>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                  <p className="text-sm text-gray-600">
                    Found {previewData.totalRows} rows with {previewData.columns.length} columns
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {previewData.columns.map((column, index) => (
                          <th
                            key={index}
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            {column}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {previewData.sampleRows.map((row, index) => (
                        <tr key={index}>
                          {previewData.columns.map((column, colIndex) => (
                            <td key={colIndex} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {row[column] || '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {previewData.totalRows > 3 && (
                  <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
                    <p className="text-sm text-gray-600 text-center">
                      Showing first 3 rows of {previewData.totalRows} total rows
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Upload Progress */}
          {isUploading && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Importing budget data...</span>
                <span className="text-gray-900">{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          )}

          {/* Error Messages */}
          {errors.import && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{errors.import}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              onClick={() => {
                onClose();
                resetState();
              }}
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium"
              disabled={isUploading}
            >
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={!file || isUploading}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUploading ? 'Importing...' : 'Import Budget Data'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BudgetImport;
